import { useCallback, useEffect, useRef, useState } from 'react';

interface Entry<T> {
  data?: T;
  error?: unknown;
  /** When the data/error was stored; 0 marks an entry as stale (refetch on next use). */
  at: number;
  /** The fetch whose result this entry is waiting for. Results of older fetches are ignored. */
  promise?: Promise<T>;
  /** Invalidated with `refetch: false`: mounted screens wait for their next mount instead of fetching. */
  idle?: boolean;
}

const cache = new Map<string, Entry<unknown>>();
const listeners = new Map<string, Set<() => void>>();

function emit(key: string) {
  listeners.get(key)?.forEach((l) => l());
}

export interface InvalidateOptions {
  /**
   * Keep showing the cached data while mounted screens refetch (pull-to-refresh).
   * By default the data is dropped, so a different account never sees it.
   */
  keepData?: boolean;
  /**
   * `false`: mounted screens do not refetch right away; each key fetches on its
   * next mount. Used around a session swap, so screens still showing the old
   * account neither hit the old server again nor run their old request
   * against the new one.
   */
  refetch?: boolean;
}

/**
 * Invalidates cached responses whose key starts with `prefix` (or everything).
 * Mounted screens refetch immediately (unless `refetch: false`); unmounted
 * keys refetch on next use. Results of fetches in flight for these keys are
 * ignored.
 */
export function invalidate(prefix = '', opts: InvalidateOptions = {}): void {
  const idle = opts.refetch === false;
  for (const [key, entry] of [...cache.entries()]) {
    if (!key.startsWith(prefix)) continue;
    if (opts.keepData && entry.data !== undefined) cache.set(key, { data: entry.data, at: 0, idle });
    else if (idle) cache.set(key, { at: 0, idle });
    else cache.delete(key);
    emit(key);
  }
}

export function setQueryData<T>(key: string, update: (prev: T | undefined) => T): void {
  const entry = cache.get(key) as Entry<T> | undefined;
  cache.set(key, { ...entry, data: update(entry?.data), error: undefined, at: entry?.at || Date.now() });
  emit(key);
}

export function getQueryData<T>(key: string): T | undefined {
  return (cache.get(key) as Entry<T> | undefined)?.data;
}

function fetchInto<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = cache.get(key) as Entry<T> | undefined;
  if (existing?.promise) return existing.promise;
  const promise: Promise<T> = fn().then(
    (data) => {
      if (cache.get(key)?.promise === promise) {
        cache.set(key, { data, at: Date.now() });
        emit(key);
      }
      return data;
    },
    (error: unknown) => {
      const current = cache.get(key) as Entry<T> | undefined;
      if (current?.promise === promise) {
        cache.set(key, { data: current.data, error, at: Date.now() });
        emit(key);
      }
      throw error;
    },
  );
  cache.set(key, { data: existing?.data, error: existing?.error, at: existing?.at ?? 0, promise });
  return promise;
}

function needsFetch(entry: Entry<unknown> | undefined, staleMs: number): boolean {
  if (!entry) return true;
  if (entry.promise) return false;
  return entry.at === 0 || Date.now() - entry.at > staleMs || entry.error !== undefined;
}

/** Starts a fetch unless fresh data (younger than `staleMs`) or a fetch in flight exists. */
export function prefetchQuery<T>(key: string, fn: () => Promise<T>, opts: { staleMs?: number } = {}): void {
  if (needsFetch(cache.get(key), opts.staleMs ?? 60_000)) fetchInto(key, fn).catch(() => undefined);
}

/** Resolves with fresh cached data, joins a fetch in flight, or fetches. */
export function fetchQuery<T>(key: string, fn: () => Promise<T>, opts: { staleMs?: number } = {}): Promise<T> {
  const entry = cache.get(key) as Entry<T> | undefined;
  if (!needsFetch(entry, opts.staleMs ?? 60_000) && entry?.data !== undefined && !entry.promise) return Promise.resolve(entry.data);
  return fetchInto(key, fn);
}

export interface QueryResult<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  refreshing: boolean;
  refresh: () => Promise<void>;
}

/**
 * Stale-while-revalidate data hook. Cached data renders immediately while a
 * background refresh runs, so navigating back and forth feels instant.
 */
export function useQuery<T>(key: string | null, fn: () => Promise<T>, opts: { staleMs?: number } = {}): QueryResult<T> {
  const staleMs = opts.staleMs ?? 60_000;
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const [, force] = useState(0);

  useEffect(() => {
    if (!key) return;
    const set = listeners.get(key) ?? new Set();
    const l = () => {
      // Invalidated while mounted: refetch instead of waiting for a remount.
      const entry = cache.get(key);
      if (!entry || (entry.at === 0 && !entry.promise && !entry.idle)) fetchInto(key, () => fnRef.current()).catch(() => undefined);
      force((n) => n + 1);
    };
    set.add(l);
    listeners.set(key, set);
    if (needsFetch(cache.get(key), staleMs)) {
      fetchInto(key, () => fnRef.current()).catch(() => undefined);
      force((n) => n + 1);
    }
    return () => {
      set.delete(l);
      if (set.size === 0) listeners.delete(key);
    };
  }, [key, staleMs]);

  const refresh = useCallback(async () => {
    if (!key) return;
    const entry = cache.get(key);
    if (entry) cache.set(key, { data: entry.data, error: entry.error, at: entry.at });
    const pending = fetchInto(key, () => fnRef.current());
    emit(key);
    await pending.catch(() => undefined);
  }, [key]);

  const entry = key ? (cache.get(key) as Entry<T> | undefined) : undefined;
  return {
    data: entry?.data,
    error: entry?.error,
    loading: !!key && entry?.data === undefined && entry?.error === undefined,
    refreshing: !!entry?.promise,
    refresh,
  };
}

/** Test hook: forget every cached response. */
export function __resetQueryCacheForTests(): void {
  cache.clear();
}
